// pages/Admin/AbstractPageAdmin.jsx
// Route: /abstract-admin
// Manages: Abstractinfo section content + FAQs

import React, { useState, useEffect, useRef } from "react";
import axios from "../../api/axiosInstance";
import App from "../../Utils/LayoutMobile";

/* ─── Tokens ─────────────────────────────────────────────── */
const C_PINK   = "#F04DA0";
const C_PURPLE = "#8B2FC9";
const C_DARK   = "#1a0040";
const C_GRAD   = `linear-gradient(135deg,${C_PINK},${C_PURPLE})`;

/* ─── Toast ──────────────────────────────────────────────── */
let _tid = 0;
function useToast() {
  const [toasts, setToasts] = useState([]);
  const add = (msg, type = "success") => {
    const id = ++_tid;
    setToasts((p) => [...p, { id, msg, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 3500);
  };
  return {
    toasts,
    success: (m) => add(m, "success"),
    error:   (m) => add(m, "error"),
  };
}

/* ─── UploadField ─────────────────────────────────────────── */
function UploadField({ label, fileList, onChange, hint }) {
  const ref  = useRef();
  const file = fileList?.[0];

  const handle = (files) => {
    if (!files?.length) return;
    const f = files[0];
    onChange([{ uid: Date.now(), name: f.name, originFileObj: f, url: URL.createObjectURL(f) }]);
  };

  return (
    <div className="ab-field">
      {label && <label className="ab-label">{label}</label>}
      <div
        className={`ab-upload ${file ? "ab-upload--has" : ""}`}
        onClick={() => ref.current?.click()}
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("ab-upload--drag"); }}
        onDragLeave={(e) => e.currentTarget.classList.remove("ab-upload--drag")}
        onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("ab-upload--drag"); handle(e.dataTransfer.files); }}
      >
        <input ref={ref} type="file" accept="image/*" style={{ display: "none" }}
          onChange={(e) => handle(e.target.files)} />
        {file ? (
          <div className="ab-upload-prev">
            <img src={file.url} alt="preview" className="ab-upload-thumb" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <span className="ab-upload-name">{file.name}</span>
              <span className="ab-upload-change">Click to change</span>
            </div>
            <button className="ab-icon-btn" style={{ flexShrink: 0 }}
              onClick={(e) => { e.stopPropagation(); onChange([]); }}>
              <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>
        ) : (
          <div className="ab-upload-empty">
            <svg width="20" height="20" fill="none" stroke={C_PINK} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" />
              <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3" />
            </svg>
            <span>Drop or <strong>browse</strong></span>
            {hint && <span className="ab-upload-hint">{hint}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── SectionCard ─────────────────────────────────────────── */
function SectionCard({ title, subtitle, icon, iconColor, iconBg, delay = 0, children }) {
  return (
    <div className="ab-card" style={{ animationDelay: `${delay}s` }}>
      <div className="ab-card-hdr">
        <span className="ab-card-ico" style={{ color: iconColor, background: iconBg }}>{icon}</span>
        <div>
          <h2 className="ab-card-title">{title}</h2>
          {subtitle && <p className="ab-card-sub">{subtitle}</p>}
        </div>
      </div>
      <div className="ab-card-body">{children}</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════ */
export default function AbstractPage() {
  const toast = useToast();
  const [tab,     setTab]     = useState("info");
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [faqSaving, setFaqSaving] = useState(false);

  /* ── AbstractInfo state ── */
  const [info, setInfo] = useState({
    eyebrowText:        "Abstract Submissions",
    titleMain:          "Share Your",
    titleHighlight:     "Research & Insights",
    titleSuffix:        "With the World",
    description:        "",
    guidelinesTitle:    "Submission Guidelines",
    guidelinesSubtitle: "Ensure your abstract meets all criteria",
    guidelines:         [{ text: "" }],
    badgeText:          "Submission closes soon — submit early",
    submissionDeadline: "",
    formTitle:          "Submit Your Abstract",
    formSubtitle:       "Fill in the details below to submit your research",
    categories:         [
      { value: "clinical", label: "Clinical Research" },
      { value: "basic",    label: "Basic Science"     },
      { value: "case",     label: "Case Report"       },
      { value: "review",   label: "Review Article"    },
    ],
  });
  const [image1, setImage1] = useState([]);
  const [image2, setImage2] = useState([]);

  /* ── FAQ state ── */
  const [faqSection, setFaqSection] = useState({
    eyebrowText:    "Support & Info",
    titleMain:      "Abstract",
    titleHighlight: "FAQs",
    subtitle:       "Everything you need to know about abstract submissions.",
    faqs:           [{ q: "", a: "" }],
  });

  /* ── Fetch ── */
  useEffect(() => {
    axios.get("/api/v1/abstract-page")
      .then((res) => {
        const d = res.data?.data || {};

        /* abstract info */
        if (d.abstractInfo) {
          const ai = d.abstractInfo;
          setInfo({
            eyebrowText:        ai.eyebrowText        || "Abstract Submissions",
            titleMain:          ai.titleMain           || "Share Your",
            titleHighlight:     ai.titleHighlight      || "Research & Insights",
            titleSuffix:        ai.titleSuffix         || "With the World",
            description:        ai.description         || "",
            guidelinesTitle:    ai.guidelinesTitle     || "Submission Guidelines",
            guidelinesSubtitle: ai.guidelinesSubtitle  || "Ensure your abstract meets all criteria",
            guidelines:         ai.guidelines?.length  ? ai.guidelines : [{ text: "" }],
            badgeText:          ai.badgeText           || "Submission closes soon — submit early",
            submissionDeadline: ai.submissionDeadline
              ? new Date(ai.submissionDeadline).toISOString().split("T")[0]
              : "",
            formTitle:          ai.formTitle           || "Submit Your Abstract",
            formSubtitle:       ai.formSubtitle        || "",
            categories:         ai.categories?.length  ? ai.categories : [
              { value: "clinical", label: "Clinical Research" },
              { value: "basic",    label: "Basic Science"     },
              { value: "case",     label: "Case Report"       },
              { value: "review",   label: "Review Article"    },
            ],
          });
          if (ai.image1Url) setImage1([{ uid: "-1", name: "image1.jpg", url: ai.image1Url }]);
          if (ai.image2Url) setImage2([{ uid: "-2", name: "image2.jpg", url: ai.image2Url }]);
        }

        /* faq section */
        if (d.faqSection) {
          const f = d.faqSection;
          setFaqSection({
            eyebrowText:    f.eyebrowText    || "Support & Info",
            titleMain:      f.titleMain      || "Abstract",
            titleHighlight: f.titleHighlight || "FAQs",
            subtitle:       f.subtitle       || "",
            faqs:           f.faqs?.length   ? f.faqs : [{ q: "", a: "" }],
          });
        }
      })
      .catch(() => toast.error("Failed to load abstract page data"))
      .finally(() => setLoading(false));
  }, []);

  /* ── Guideline helpers ── */
  const addGuideline    = () => setInfo((p) => ({ ...p, guidelines: [...p.guidelines, { text: "" }] }));
  const removeGuideline = (i) => setInfo((p) => ({ ...p, guidelines: p.guidelines.filter((_, idx) => idx !== i) }));
  const changeGuideline = (i, v) => setInfo((p) => {
    const g = [...p.guidelines]; g[i] = { text: v }; return { ...p, guidelines: g };
  });

  /* ── Category helpers ── */
  const addCategory    = () => setInfo((p) => ({ ...p, categories: [...p.categories, { value: "", label: "" }] }));
  const removeCategory = (i) => setInfo((p) => ({ ...p, categories: p.categories.filter((_, idx) => idx !== i) }));
  const changeCategory = (i, k, v) => setInfo((p) => {
    const c = [...p.categories]; c[i] = { ...c[i], [k]: v }; return { ...p, categories: c };
  });

  /* ── FAQ helpers ── */
  const addFaq    = ()      => setFaqSection((p) => ({ ...p, faqs: [...p.faqs, { q: "", a: "" }] }));
  const removeFaq = (i)     => setFaqSection((p) => ({ ...p, faqs: p.faqs.filter((_, idx) => idx !== i) }));
  const updFaq    = (i,k,v) => setFaqSection((p) => { const f=[...p.faqs]; f[i]={...f[i],[k]:v}; return {...p,faqs:f}; });

  /* ── Save info ── */
  const saveInfo = async () => {
    setSaving(true);
    try {
      const fd = new FormData();

      // All text fields
      Object.entries(info).forEach(([k, v]) => {
        if (k === "guidelines" || k === "categories") {
          fd.append(k, JSON.stringify(v));
        } else {
          fd.append(k, v ?? "");
        }
      });

      // Images
      const img1 = image1?.[0];
      if (img1?.originFileObj) fd.append("image1", img1.originFileObj);
      else if (img1?.url)      fd.append("image1Url", img1.url);

      const img2 = image2?.[0];
      if (img2?.originFileObj) fd.append("image2", img2.originFileObj);
      else if (img2?.url)      fd.append("image2Url", img2.url);

      await axios.post("/api/v1/abstract-page/info", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Abstract info saved!");
    } catch (err) {
      toast.error(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  /* ── Save FAQs ── */
  const saveFaqs = async () => {
    const invalid = faqSection.faqs.some((f) => !f.q.trim() || !f.a.trim());
    if (invalid) { toast.error("All FAQ questions and answers must be filled"); return; }
    setFaqSaving(true);
    try {
      await axios.post("/api/v1/abstract-page/faqs", faqSection);
      toast.success("FAQs saved!");
    } catch (err) {
      toast.error(err?.message || "Save failed");
    } finally {
      setFaqSaving(false);
    }
  };

  /* ── Deadline display helper ── */
  const formatDeadline = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric", month: "long", year: "numeric",
    });
  };

  /* ── Days remaining helper ── */
  const daysRemaining = (dateStr) => {
    if (!dateStr) return null;
    const diff = new Date(dateStr) - new Date();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days;
  };

  /* ── Icons ── */
  const InfoIcon  = <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>;
  const FaqIcon   = <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>;
  const CalIcon   = <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
  const ImgIcon   = <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;
  const FormIcon  = <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
  const PlusIcon  = <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
  const TrashIcon = <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>;
  const SaveIcon  = <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>;

  const days = daysRemaining(info.submissionDeadline);

  return (
    <App>
      <div className="ab-page">

        {/* Toasts */}
        <div className="ab-toast-wrap">
          {toast.toasts.map((t) => (
            <div key={t.id} className={`ab-toast ab-toast--${t.type}`}>
              {t.type === "success"
                ? <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                : <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              }
              <span>{t.msg}</span>
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="ab-header">
          <div className="ab-eyebrow"><span className="ab-eyebrow-dot" />Abstract Page</div>
          <div className="ab-header-row">
            <div>
              <h1 className="ab-page-title">Abstract <span className="ab-grad">Content</span></h1>
              <p className="ab-page-sub">Manage the Abstract submission section, images, deadline date, guidelines, categories, and FAQs.</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="ab-loading"><div className="ab-spinner" /><span>Loading…</span></div>
        ) : (
          <>
            {/* Tabs */}
            <div className="ab-tabs">
              <button className={`ab-tab ${tab === "info" ? "ab-tab--active" : ""}`} onClick={() => setTab("info")}>
                {InfoIcon} Abstract Info
              </button>
              <button className={`ab-tab ${tab === "faqs" ? "ab-tab--active" : ""}`} onClick={() => setTab("faqs")}>
                {FaqIcon} FAQs
                <span className="ab-tab-badge">{faqSection.faqs.length}</span>
              </button>
            </div>

            {/* ══ INFO TAB ══ */}
            {tab === "info" && (
              <div className="ab-panel">

                {/* ── 1. Hero Section Text ── */}
                <SectionCard title="Hero Section Text"
                  subtitle="Eyebrow badge, title (3 parts), and description paragraph"
                  icon={InfoIcon} iconColor={C_PINK} iconBg="rgba(240,77,160,0.1)" delay={0.05}>

                  <div className="ab-grid-3">
                    <div className="ab-field">
                      <label className="ab-label">Eyebrow Badge</label>
                      <input className="ab-input" value={info.eyebrowText}
                        onChange={(e) => setInfo((p) => ({ ...p, eyebrowText: e.target.value }))}
                        placeholder="Abstract Submissions" />
                    </div>
                    <div className="ab-field">
                      <label className="ab-label">Title — Main (dark)</label>
                      <input className="ab-input" value={info.titleMain}
                        onChange={(e) => setInfo((p) => ({ ...p, titleMain: e.target.value }))}
                        placeholder="Share Your" />
                    </div>
                    <div className="ab-field">
                      <label className="ab-label">Title — Gradient (pink)</label>
                      <input className="ab-input" value={info.titleHighlight}
                        onChange={(e) => setInfo((p) => ({ ...p, titleHighlight: e.target.value }))}
                        placeholder="Research & Insights" />
                    </div>
                  </div>

                  <div className="ab-field">
                    <label className="ab-label">Title — Suffix (second line, dark)</label>
                    <input className="ab-input" value={info.titleSuffix}
                      onChange={(e) => setInfo((p) => ({ ...p, titleSuffix: e.target.value }))}
                      placeholder="With the World" />
                  </div>

                  <div className="ab-field">
                    <label className="ab-label">Description Paragraph</label>
                    <textarea className="ab-textarea" rows={4} value={info.description}
                      onChange={(e) => setInfo((p) => ({ ...p, description: e.target.value }))}
                      placeholder="Skinola International Aesthetics invites clinicians, researchers, and medical professionals to submit original abstracts…" />
                  </div>
                </SectionCard>

                {/* ── 2. Submission Deadline ── */}
                <SectionCard title="Submission Deadline"
                  subtitle="Set the deadline date shown in the badge and available for countdown on frontend"
                  icon={CalIcon} iconColor={C_PURPLE} iconBg="rgba(139,47,201,0.1)" delay={0.08}>

                  <div className="ab-grid-2">
                    <div className="ab-field">
                      <label className="ab-label">Deadline Date</label>
                      <div className="ab-date-wrap">
                        <svg className="ab-date-ico" width="15" height="15" fill="none" stroke={C_PINK} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                          <rect x="3" y="4" width="18" height="18" rx="2"/>
                          <line x1="16" y1="2" x2="16" y2="6"/>
                          <line x1="8" y1="2" x2="8" y2="6"/>
                          <line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                        <input type="date" className="ab-input ab-input--date"
                          value={info.submissionDeadline}
                          onChange={(e) => setInfo((p) => ({ ...p, submissionDeadline: e.target.value }))} />
                      </div>
                    </div>
                    <div className="ab-field">
                      <label className="ab-label">Badge Text (shown below guidelines)</label>
                      <input className="ab-input" value={info.badgeText}
                        onChange={(e) => setInfo((p) => ({ ...p, badgeText: e.target.value }))}
                        placeholder="Submission closes soon — submit early" />
                    </div>
                  </div>

                  {/* Live deadline preview */}
                  {info.submissionDeadline && (
                    <div className="ab-deadline-preview">
                      <div className="ab-deadline-card">
                        <div className="ab-deadline-icon">{CalIcon}</div>
                        <div>
                          <div className="ab-deadline-date">{formatDeadline(info.submissionDeadline)}</div>
                          <div className="ab-deadline-label">Abstract Submission Deadline</div>
                        </div>
                        <div className={`ab-deadline-days ${days <= 7 ? "ab-deadline-days--urgent" : days <= 30 ? "ab-deadline-days--warn" : ""}`}>
                          {days > 0 ? (
                            <><span className="ab-days-num">{days}</span><span className="ab-days-lbl">days left</span></>
                          ) : (
                            <span className="ab-days-expired">Expired</span>
                          )}
                        </div>
                      </div>
                      <div className="ab-badge-preview">
                        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                          <rect x="3" y="4" width="18" height="18" rx="2"/>
                          <line x1="16" y1="2" x2="16" y2="6"/>
                          <line x1="8" y1="2" x2="8" y2="6"/>
                          <line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                        {info.badgeText}
                      </div>
                    </div>
                  )}
                </SectionCard>

                {/* ── 3. Guidelines Card ── */}
                <SectionCard title="Submission Guidelines Card"
                  subtitle="Title, subtitle, and bullet point list shown in the guidelines card"
                  icon={InfoIcon} iconColor={C_PINK} iconBg="rgba(240,77,160,0.1)" delay={0.1}>

                  <div className="ab-grid-2">
                    <div className="ab-field">
                      <label className="ab-label">Card Title</label>
                      <input className="ab-input" value={info.guidelinesTitle}
                        onChange={(e) => setInfo((p) => ({ ...p, guidelinesTitle: e.target.value }))}
                        placeholder="Submission Guidelines" />
                    </div>
                    <div className="ab-field">
                      <label className="ab-label">Card Subtitle</label>
                      <input className="ab-input" value={info.guidelinesSubtitle}
                        onChange={(e) => setInfo((p) => ({ ...p, guidelinesSubtitle: e.target.value }))}
                        placeholder="Ensure your abstract meets all criteria" />
                    </div>
                  </div>

                  <div className="ab-sub-hdr">
                    <div>
                      <p className="ab-sub-title">Guideline Points</p>
                      <p className="ab-sub-desc">Each item appears as a bullet in the 2-column grid</p>
                    </div>
                    <button className="ab-add-btn" onClick={addGuideline}>{PlusIcon} Add Point</button>
                  </div>

                  <div className="ab-list">
                    {info.guidelines.map((g, i) => (
                      <div key={i} className="ab-list-row">
                        <div className="ab-list-num">{i + 1}</div>
                        <input className="ab-input" style={{ flex: 1 }}
                          value={g.text}
                          onChange={(e) => changeGuideline(i, e.target.value)}
                          placeholder="e.g. Original and unpublished research only" />
                        {info.guidelines.length > 1 && (
                          <button className="ab-icon-btn ab-icon-btn--del" onClick={() => removeGuideline(i)}>{TrashIcon}</button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Guidelines preview */}
                  {info.guidelines.some((g) => g.text) && (
                    <div className="ab-guidelines-preview">
                      <p className="ab-preview-lbl">Preview (2-column grid)</p>
                      <div className="ab-guidelines-grid">
                        {info.guidelines.filter((g) => g.text).map((g, i) => (
                          <div key={i} className="ab-guideline-item-preview">
                            <span className="ab-bullet-dot" />
                            <span>{g.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </SectionCard>

                {/* ── 4. Images ── */}
                <SectionCard title="Section Images"
                  subtitle="Image 1 — right side (row 1, next to guidelines). Image 2 — left side (row 2, next to form)"
                  icon={ImgIcon} iconColor={C_PURPLE} iconBg="rgba(139,47,201,0.1)" delay={0.12}>

                  <div className="ab-grid-2">
                    <UploadField label="Image 1 — Row 1 (right side)"
                      hint="Recommended: 900×500px, conference/event photo"
                      fileList={image1} onChange={setImage1} />
                    <UploadField label="Image 2 — Row 2 (left side)"
                      hint="Recommended: 900×500px, doctor/research photo"
                      fileList={image2} onChange={setImage2} />
                  </div>

                  {(image1[0] || image2[0]) && (
                    <div className="ab-img-strip">
                      <p className="ab-preview-lbl">Preview</p>
                      <div className="ab-img-strip-thumbs">
                        {image1[0] && (
                          <div className="ab-img-strip-thumb">
                            <img src={image1[0].url} alt="img1" />
                            <span>Image 1 (Row 1)</span>
                          </div>
                        )}
                        {image2[0] && (
                          <div className="ab-img-strip-thumb">
                            <img src={image2[0].url} alt="img2" />
                            <span>Image 2 (Row 2)</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </SectionCard>

                {/* ── 5. Form Section ── */}
                <SectionCard title="Form Section Header & Categories"
                  subtitle="Form top-bar title, subtitle, and dynamic dropdown categories"
                  icon={FormIcon} iconColor={C_PINK} iconBg="rgba(240,77,160,0.1)" delay={0.14}>

                  <div className="ab-grid-2">
                    <div className="ab-field">
                      <label className="ab-label">Form Header Title</label>
                      <input className="ab-input" value={info.formTitle}
                        onChange={(e) => setInfo((p) => ({ ...p, formTitle: e.target.value }))}
                        placeholder="Submit Your Abstract" />
                    </div>
                    <div className="ab-field">
                      <label className="ab-label">Form Header Subtitle</label>
                      <input className="ab-input" value={info.formSubtitle}
                        onChange={(e) => setInfo((p) => ({ ...p, formSubtitle: e.target.value }))}
                        placeholder="Fill in the details below to submit your research" />
                    </div>
                  </div>

                  <div className="ab-sub-hdr">
                    <div>
                      <p className="ab-sub-title">Category Dropdown Options</p>
                      <p className="ab-sub-desc">These appear in the "Category" select field on the submission form</p>
                    </div>
                    <button className="ab-add-btn" onClick={addCategory}>{PlusIcon} Add Category</button>
                  </div>

                  <div className="ab-list">
                    {info.categories.map((cat, i) => (
                      <div key={i} className="ab-list-row">
                        <div className="ab-list-num ab-list-num--purple">#{i + 1}</div>
                        <div className="ab-grid-2" style={{ flex: 1 }}>
                          <input className="ab-input" value={cat.value}
                            onChange={(e) => changeCategory(i, "value", e.target.value)}
                            placeholder="value (e.g. clinical)" />
                          <input className="ab-input" value={cat.label}
                            onChange={(e) => changeCategory(i, "label", e.target.value)}
                            placeholder="Label (e.g. Clinical Research)" />
                        </div>
                        {info.categories.length > 1 && (
                          <button className="ab-icon-btn ab-icon-btn--del" onClick={() => removeCategory(i)}>{TrashIcon}</button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Category preview */}
                  <div className="ab-cat-preview">
                    <p className="ab-preview-lbl">Dropdown Preview</p>
                    <select className="ab-select-preview" defaultValue="">
                      <option value="" disabled>Select category</option>
                      {info.categories.filter((c) => c.label).map((cat, i) => (
                        <option key={i} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                </SectionCard>

                {/* Save bar */}
                <div className="ab-save-bar">
                  <div className="ab-save-info">
                    <svg width="13" height="13" fill="none" stroke="#9a86b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    Saves all Abstract Info (text, images, deadline, guidelines, categories).
                  </div>
                  <button className="ab-save-btn" onClick={saveInfo} disabled={saving}>
                    {saving ? <><span className="ab-spin" /><span>Saving…</span></> : <>{SaveIcon}<span>Save Abstract Info</span></>}
                  </button>
                </div>
              </div>
            )}

            {/* ══ FAQs TAB ══ */}
            {tab === "faqs" && (
              <div className="ab-panel">

                <SectionCard title="FAQ Section Header"
                  subtitle="Eyebrow, title, and subtitle shown above the accordion"
                  icon={FaqIcon} iconColor={C_PINK} iconBg="rgba(240,77,160,0.1)" delay={0.05}>

                  <div className="ab-grid-3">
                    <div className="ab-field">
                      <label className="ab-label">Eyebrow</label>
                      <input className="ab-input" value={faqSection.eyebrowText}
                        onChange={(e) => setFaqSection((p) => ({ ...p, eyebrowText: e.target.value }))}
                        placeholder="Support & Info" />
                    </div>
                    <div className="ab-field">
                      <label className="ab-label">Title — Main</label>
                      <input className="ab-input" value={faqSection.titleMain}
                        onChange={(e) => setFaqSection((p) => ({ ...p, titleMain: e.target.value }))}
                        placeholder="Abstract" />
                    </div>
                    <div className="ab-field">
                      <label className="ab-label">Title — Highlight (pink)</label>
                      <input className="ab-input" value={faqSection.titleHighlight}
                        onChange={(e) => setFaqSection((p) => ({ ...p, titleHighlight: e.target.value }))}
                        placeholder="FAQs" />
                    </div>
                  </div>

                  <div className="ab-field">
                    <label className="ab-label">Subtitle</label>
                    <textarea className="ab-textarea" rows={2} value={faqSection.subtitle}
                      onChange={(e) => setFaqSection((p) => ({ ...p, subtitle: e.target.value }))}
                      placeholder="Everything you need to know about abstract submissions." />
                  </div>
                </SectionCard>

                <SectionCard title={`Q&A Items (${faqSection.faqs.length})`}
                  subtitle="Each question-answer pair appears as an accordion on the Abstract page"
                  icon={FaqIcon} iconColor={C_PURPLE} iconBg="rgba(139,47,201,0.1)" delay={0.1}>

                  <div className="ab-faq-list">
                    {faqSection.faqs.map((faq, i) => (
                      <div key={i} className="ab-faq-row">
                        <div className="ab-faq-num">{i + 1}</div>
                        <div className="ab-faq-inputs">
                          <div className="ab-field">
                            <label className="ab-label">Question</label>
                            <input className="ab-input" value={faq.q}
                              onChange={(e) => updFaq(i, "q", e.target.value)}
                              placeholder="Who is eligible to submit an abstract?"
                              style={{ fontWeight: 600 }} />
                          </div>
                          <div className="ab-field">
                            <label className="ab-label">Answer</label>
                            <textarea className="ab-textarea" rows={3} value={faq.a}
                              onChange={(e) => updFaq(i, "a", e.target.value)}
                              placeholder="We welcome submissions from practicing dermatologists…" />
                          </div>
                        </div>
                        {faqSection.faqs.length > 1 && (
                          <button className="ab-icon-btn ab-icon-btn--del" style={{ marginTop: 28 }}
                            onClick={() => removeFaq(i)}>{TrashIcon}</button>
                        )}
                      </div>
                    ))}
                  </div>

                  <button className="ab-add-btn ab-add-btn--full" onClick={addFaq}>
                    {PlusIcon} Add FAQ
                  </button>
                </SectionCard>

                <div className="ab-save-bar">
                  <div className="ab-save-info">
                    <svg width="13" height="13" fill="none" stroke="#9a86b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    Saves FAQ section header and all Q&A items.
                  </div>
                  <button className="ab-save-btn" onClick={saveFaqs} disabled={faqSaving}>
                    {faqSaving ? <><span className="ab-spin" /><span>Saving…</span></> : <>{SaveIcon}<span>Save FAQs</span></>}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        @keyframes ab-fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        @keyframes ab-spin-anim{to{transform:rotate(360deg)}}
        @keyframes ab-pulse{0%,100%{opacity:1}50%{opacity:.4}}
        @keyframes ab-toast-in{from{opacity:0;transform:translateX(28px)}to{opacity:1;transform:translateX(0)}}
        *,*::before,*::after{box-sizing:border-box}

        .ab-page{background:#f4eefa;min-height:100vh;padding:28px 32px 80px;display:flex;flex-direction:column;gap:22px;font-family:inherit;}
        .ab-header{animation:ab-fadeUp .4s ease both;}
        .ab-eyebrow{display:inline-flex;align-items:center;gap:7px;font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:${C_PINK};background:rgba(240,77,160,0.08);border:1px solid rgba(240,77,160,0.2);padding:5px 14px 5px 10px;border-radius:50px;margin-bottom:12px;}
        .ab-eyebrow-dot{width:6px;height:6px;border-radius:50%;background:${C_PINK};animation:ab-pulse 2s infinite;flex-shrink:0;}
        .ab-header-row{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;flex-wrap:wrap;}
        .ab-page-title{font-size:clamp(22px,2.5vw,30px);font-weight:800;color:${C_DARK};letter-spacing:-.025em;margin:0 0 6px;}
        .ab-grad{background:linear-gradient(90deg,${C_PINK},#c066f0);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
        .ab-page-sub{font-size:13.5px;color:#8a7a9a;margin:0;}

        .ab-loading{display:flex;align-items:center;gap:12px;padding:60px;justify-content:center;color:#8a7a9a;font-size:14px;}
        .ab-spinner{width:28px;height:28px;border-radius:50%;border:3px solid rgba(240,77,160,0.15);border-top-color:${C_PINK};animation:ab-spin-anim .75s linear infinite;}

        /* Tabs */
        .ab-tabs{display:flex;gap:8px;flex-wrap:wrap;}
        .ab-tab{display:inline-flex;align-items:center;gap:7px;padding:10px 20px;border-radius:12px;border:1.5px solid #e8d8f8;background:#fff;color:#8a7a9a;font-size:13px;font-weight:700;cursor:pointer;transition:all .2s;font-family:inherit;}
        .ab-tab--active{background:${C_GRAD};color:#fff;border-color:transparent;box-shadow:0 6px 18px rgba(240,77,160,0.28);}
        .ab-tab:not(.ab-tab--active):hover{border-color:${C_PINK};color:${C_PINK};}
        .ab-tab-badge{display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:50%;background:rgba(255,255,255,0.25);font-size:10px;font-weight:800;color:#fff;}
        .ab-tab:not(.ab-tab--active) .ab-tab-badge{background:rgba(240,77,160,0.12);color:${C_PINK};}

        /* Panel */
        .ab-panel{display:flex;flex-direction:column;gap:20px;}

        /* Section card */
        .ab-card{background:#fff;border-radius:20px;border:1px solid #ede5f5;box-shadow:0 4px 20px rgba(26,0,64,.06);overflow:hidden;animation:ab-fadeUp .4s ease both;}
        .ab-card-hdr{display:flex;align-items:center;gap:14px;padding:20px 26px 18px;border-bottom:1px solid #f3ecfa;background:#faf6fe;}
        .ab-card-ico{width:44px;height:44px;border-radius:12px;flex-shrink:0;display:flex;align-items:center;justify-content:center;}
        .ab-card-title{font-size:16px;font-weight:800;color:${C_DARK};margin:0 0 2px;}
        .ab-card-sub{font-size:12px;color:#9a86b8;margin:0;}
        .ab-card-body{padding:24px 26px 28px;display:flex;flex-direction:column;gap:18px;}

        /* Fields */
        .ab-field{display:flex;flex-direction:column;gap:6px;}
        .ab-label{font-size:12px;font-weight:700;color:#5a5070;letter-spacing:.03em;}
        .ab-input{height:44px;border-radius:11px;border:1.5px solid #e8d8f8;padding:0 14px;font-size:13.5px;color:${C_DARK};background:#fdfbff;outline:none;font-family:inherit;width:100%;transition:border-color .2s,box-shadow .2s;}
        .ab-input:focus{border-color:${C_PINK};box-shadow:0 0 0 3px rgba(240,77,160,0.12);background:#fff;}
        .ab-input::placeholder{color:#c4b4d8;}
        .ab-textarea{border-radius:11px;border:1.5px solid #e8d8f8;padding:12px 14px;font-size:13.5px;color:${C_DARK};background:#fdfbff;outline:none;font-family:inherit;width:100%;resize:vertical;line-height:1.6;transition:border-color .2s,box-shadow .2s;}
        .ab-textarea:focus{border-color:${C_PINK};box-shadow:0 0 0 3px rgba(240,77,160,0.12);background:#fff;}
        .ab-textarea::placeholder{color:#c4b4d8;}
        .ab-input--date{padding-left:38px;cursor:pointer;}
        .ab-input--date::-webkit-calendar-picker-indicator{opacity:.5;cursor:pointer;}
        .ab-date-wrap{position:relative;}
        .ab-date-ico{position:absolute;left:13px;top:50%;transform:translateY(-50%);pointer-events:none;}
        .ab-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
        .ab-grid-3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:14px;}

        /* Upload */
        .ab-upload{border:1.5px dashed #d8c8f0;border-radius:12px;background:#fdfbff;padding:14px;cursor:pointer;min-height:72px;display:flex;align-items:center;transition:border-color .2s,background .2s;}
        .ab-upload:hover,.ab-upload--drag{border-color:${C_PINK};background:rgba(240,77,160,0.03);}
        .ab-upload--has{border-style:solid;border-color:#e8d8f8;background:#fff;}
        .ab-upload-empty{display:flex;flex-direction:column;align-items:center;gap:6px;width:100%;text-align:center;font-size:12px;color:#8a7a9a;}
        .ab-upload-empty strong{color:${C_PINK};}
        .ab-upload-hint{font-size:11px;color:#b4a4c8;}
        .ab-upload-prev{display:flex;align-items:center;gap:10px;width:100%;}
        .ab-upload-thumb{width:44px;height:44px;object-fit:cover;border-radius:7px;border:1px solid #ede5f5;flex-shrink:0;}
        .ab-upload-name{display:block;font-size:12px;font-weight:700;color:${C_DARK};overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
        .ab-upload-change{font-size:11px;color:#9a86b8;display:block;}

        /* Deadline preview */
        .ab-deadline-preview{display:flex;flex-direction:column;gap:12px;}
        .ab-deadline-card{background:#faf6fe;border:1px solid #ede5f5;border-radius:14px;padding:16px 20px;display:flex;align-items:center;gap:16px;}
        .ab-deadline-icon{width:40px;height:40px;border-radius:10px;background:rgba(240,77,160,0.1);display:flex;align-items:center;justify-content:center;color:${C_PINK};flex-shrink:0;}
        .ab-deadline-date{font-size:16px;font-weight:800;color:${C_DARK};}
        .ab-deadline-label{font-size:12px;color:#9a86b8;margin-top:2px;}
        .ab-deadline-days{margin-left:auto;text-align:center;display:flex;flex-direction:column;align-items:center;gap:2px;}
        .ab-days-num{font-size:28px;font-weight:800;background:${C_GRAD};-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;line-height:1;}
        .ab-days-lbl{font-size:10px;font-weight:700;color:#9a86b8;text-transform:uppercase;letter-spacing:.06em;}
        .ab-deadline-days--warn .ab-days-num{background:linear-gradient(135deg,#f59e0b,#ef4444);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
        .ab-deadline-days--urgent .ab-days-num{background:linear-gradient(135deg,#ef4444,#dc2626);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
        .ab-days-expired{font-size:13px;font-weight:700;color:#ef4444;}
        .ab-badge-preview{display:inline-flex;align-items:center;gap:8px;background:#eed3f1;color:#4a1171;font-size:13px;font-weight:700;padding:9px 18px;border-radius:50px;border:1.5px solid rgba(107,25,163,0.14);align-self:flex-start;}

        /* Guidelines preview */
        .ab-guidelines-preview{background:#faf6fe;border:1px solid #ede5f5;border-radius:12px;padding:16px 18px;}
        .ab-preview-lbl{font-size:11px;font-weight:700;color:#9a86b8;text-transform:uppercase;letter-spacing:.08em;margin:0 0 12px;}
        .ab-guidelines-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px 16px;}
        .ab-guideline-item-preview{display:flex;align-items:flex-start;gap:8px;font-size:12.5px;font-weight:600;color:#4a1171;}
        .ab-bullet-dot{width:9px;height:9px;border-radius:50%;background:#c026d3;flex-shrink:0;margin-top:3px;}

        /* Image strip */
        .ab-img-strip{background:#faf6fe;border:1px solid #ede5f5;border-radius:12px;padding:14px 16px;}
        .ab-img-strip-thumbs{display:flex;gap:14px;}
        .ab-img-strip-thumb{display:flex;flex-direction:column;align-items:center;gap:6px;}
        .ab-img-strip-thumb img{width:140px;height:90px;object-fit:cover;border-radius:9px;border:1px solid #ede5f5;}
        .ab-img-strip-thumb span{font-size:11px;font-weight:600;color:#8a7a9a;}

        /* Category preview */
        .ab-cat-preview{background:#faf6fe;border:1px solid #ede5f5;border-radius:12px;padding:14px 16px;}
        .ab-select-preview{height:44px;border-radius:10px;border:1.5px solid #e8d8f8;padding:0 14px;font-size:13.5px;color:${C_DARK};background:#fff;outline:none;font-family:inherit;width:100%;max-width:360px;}

        /* Sub headers & list */
        .ab-sub-hdr{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;}
        .ab-sub-title{font-size:13.5px;font-weight:700;color:${C_DARK};margin:0 0 3px;}
        .ab-sub-desc{font-size:12px;color:#9a86b8;margin:0;}
        .ab-list{display:flex;flex-direction:column;gap:10px;}
        .ab-list-row{display:flex;align-items:center;gap:10px;background:#faf6fe;border:1px solid #ede5f5;border-radius:12px;padding:10px 14px;}
        .ab-list-num{width:26px;height:26px;border-radius:7px;background:${C_GRAD};color:#fff;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;flex-shrink:0;}
        .ab-list-num--purple{background:rgba(139,47,201,0.12);color:${C_PURPLE};}

        /* FAQ list */
        .ab-faq-list{display:flex;flex-direction:column;gap:14px;}
        .ab-faq-row{display:flex;align-items:flex-start;gap:12px;background:#faf6fe;border:1px solid #ede5f5;border-radius:14px;padding:16px;}
        .ab-faq-num{width:28px;height:28px;border-radius:8px;background:${C_GRAD};color:#fff;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;flex-shrink:0;margin-top:2px;}
        .ab-faq-inputs{flex:1;display:flex;flex-direction:column;gap:10px;}

        /* Buttons */
        .ab-add-btn{display:inline-flex;align-items:center;gap:7px;padding:9px 18px;border-radius:10px;border:1.5px solid rgba(240,77,160,0.3);background:rgba(240,77,160,0.06);color:${C_PINK};font-size:13px;font-weight:700;cursor:pointer;transition:background .2s;font-family:inherit;}
        .ab-add-btn:hover{background:rgba(240,77,160,0.12);}
        .ab-add-btn--full{width:100%;justify-content:center;}
        .ab-icon-btn{width:30px;height:30px;border-radius:8px;border:1.5px solid #e8d8f8;background:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s;color:#8a7a9a;flex-shrink:0;}
        .ab-icon-btn--del:hover{border-color:#ef4444;color:#ef4444;background:#fef2f2;}

        /* Save bar */
        .ab-save-bar{display:flex;align-items:center;justify-content:space-between;background:#fff;border-radius:16px;border:1px solid #ede5f5;box-shadow:0 4px 20px rgba(26,0,64,.06);padding:18px 26px;gap:16px;flex-wrap:wrap;}
        .ab-save-info{display:flex;align-items:center;gap:8px;font-size:12px;color:#9a86b8;font-weight:500;}
        .ab-save-btn{display:inline-flex;align-items:center;gap:9px;padding:12px 26px;background:${C_GRAD};color:#fff;font-size:13.5px;font-weight:700;border:none;border-radius:12px;cursor:pointer;box-shadow:0 6px 20px rgba(240,77,160,0.3);transition:transform .2s,box-shadow .2s;font-family:inherit;white-space:nowrap;}
        .ab-save-btn:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 10px 28px rgba(240,77,160,0.42);}
        .ab-save-btn:disabled{opacity:.65;cursor:not-allowed;}
        .ab-spin{width:15px;height:15px;border-radius:50%;border:2px solid rgba(255,255,255,0.3);border-top-color:#fff;animation:ab-spin-anim .7s linear infinite;}

        /* Toasts */
        .ab-toast-wrap{position:fixed;top:20px;right:20px;z-index:9999;display:flex;flex-direction:column;gap:8px;pointer-events:none;}
        .ab-toast{display:inline-flex;align-items:center;gap:9px;padding:11px 16px;border-radius:12px;font-size:13px;font-weight:600;box-shadow:0 8px 24px rgba(26,0,64,.15);animation:ab-toast-in .35s cubic-bezier(.16,1,.3,1) both;max-width:300px;}
        .ab-toast--success{background:#f0fdf4;color:#16a34a;border:1px solid #bbf7d0;}
        .ab-toast--error{background:#fef2f2;color:#dc2626;border:1px solid #fecaca;}

        @media(max-width:768px){
          .ab-page{padding:16px 16px 60px;gap:16px;}
          .ab-grid-2,.ab-grid-3{grid-template-columns:1fr;}
          .ab-guidelines-grid{grid-template-columns:1fr;}
          .ab-save-bar{flex-direction:column;align-items:stretch;}
          .ab-save-btn{justify-content:center;}
          .ab-card-body{padding:18px;}
        }
      `}</style>
    </App>
  );
}
